// Every browser-storage key this app owns, and the one-time move that keeps
// the old ones from being lost.
//
// WHY THIS EXISTS. The slug changed from `fff` to `fsp` on 2026-09-12 and 205
// files moved with it — except these. Fourteen localStorage keys kept the old
// prefix, because renaming a stored key is not a rename: it is a silent data
// loss. A browser that has used the site holds its theme, its detail level and
// its place in the tutorial under `fff.*`, and code that starts reading `fsp.*`
// finds nothing and quietly resets all of it.
//
// So the prefix moves and the VALUES MOVE WITH IT. `migrateLegacyKeys` runs
// once, before anything reads, and after that there is nothing left to migrate.
// The `fff` prefix became `fsp`, a swap worth making only because it needed
// little rework — which holds as long as nobody loses their theme over it.
//
// The prefix is a constant here and substituted into `index.html` at build
// time, for the same reason the product name is: the first-paint script reads
// two of these keys before any module loads, and a key typed into the HTML by
// hand is a key that drifts. That already happened once with `<title>`.

/** The prefix every key this app writes carries. Frozen, and not the name. */
export const STORAGE_PREFIX = 'fsp.';

/**
 * Prefixes we have used before.
 *
 * **This list grows; it never shrinks** — the same rule as the retired names in
 * `lint:branding`. A browser that has not been opened since the old prefix was
 * current still deserves its theme back, however long that takes.
 */
export const LEGACY_PREFIXES: readonly string[] = ['fff.'];

/** A key in our namespace. Never write a bare string. */
export function storageKey(name: string): string {
  return `${STORAGE_PREFIX}${name}`;
}

/**
 * Move everything under a retired prefix to the current one, once.
 *
 * Takes the storage rather than reaching for the global so it can be tested
 * without a DOM, which is the same reason the theme and detail modules take
 * their inputs explicitly.
 *
 * Three rules, each of which is a way this could go wrong:
 *
 *   - **An existing current key always wins.** If both `fsp.theme.v1` and
 *     `fff.theme.v1` are present, the new one is what the app has been using
 *     and the old one is a fossil. Overwriting would roll somebody back.
 *   - **The old key is removed either way**, so the migration converges. A
 *     migration that keeps finding work is one that runs forever.
 *   - **A failure is not fatal.** Private mode, a full quota, or storage
 *     blocked outright all mean the app opens with defaults, which is the same
 *     thing that happens to a first-time visitor. It is never worth a crash.
 *
 * @returns the keys that were moved, newest name, for the caller to log or test.
 */
export function migrateLegacyKeys(storage: Storage): string[] {
  const moved: string[] = [];
  try {
    // Collect first: removing while iterating an index-based API reshuffles it
    // under us and silently skips half the keys.
    const stale: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key !== null && LEGACY_PREFIXES.some((p) => key.startsWith(p))) stale.push(key);
    }

    for (const old of stale) {
      const prefix = LEGACY_PREFIXES.find((p) => old.startsWith(p)) ?? '';
      const next = `${STORAGE_PREFIX}${old.slice(prefix.length)}`;
      const value = storage.getItem(old);
      if (value !== null && storage.getItem(next) === null) {
        storage.setItem(next, value);
        moved.push(next);
      }
      storage.removeItem(old);
    }
  } catch {
    // Storage unavailable. The app opens at its defaults, exactly as it does
    // for somebody who has never been here.
  }
  return moved;
}
